/**
 * Extracción de texto de documentos adjuntados por el profesor (modo aide-prof).
 * Soporta PDF (con capa de texto) y archivos de texto plano (.txt).
 * Todo ocurre en el navegador: el archivo NO se sube a ningún servidor.
 *
 * Nota: los PDF escaneados (imágenes sin capa de texto) devolverán texto vacío
 * o muy pobre — eso requeriría OCR, que queda para una iteración posterior.
 */

import * as pdfjsLib from 'pdfjs-dist';
// Vite resuelve esto a una URL del worker empaquetado.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface ExtractedDocument {
  filename: string;
  text: string;
  /** true si el archivo se leyó pero no se obtuvo texto (p. ej. PDF escaneado). */
  empty: boolean;
}

/** Tamaño máximo del archivo aceptado (10 MB). */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function isSupportedDocument(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.pdf') || name.endsWith('.txt');
}

async function extractPdfText(file: File): Promise<string> {
  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const parts: string[] = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: any) => (typeof item?.str === 'string' ? item.str : ''))
      .join(' ');
    if (pageText.trim()) parts.push(pageText);
  }
  return parts.join('\n\n').replace(/[ \t]+/g, ' ').trim();
}

export async function extractTextFromFile(file: File): Promise<ExtractedDocument> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('El archivo es muy grande (máximo 10 MB).');
  }
  const lower = file.name.toLowerCase();

  let text = '';
  if (lower.endsWith('.txt')) {
    text = (await file.text()).trim();
  } else if (lower.endsWith('.pdf')) {
    text = await extractPdfText(file);
  } else {
    throw new Error('Formato no soportado. Adjunta un PDF o un archivo de texto (.txt).');
  }

  return { filename: file.name, text, empty: text.length === 0 };
}
